import { TransactionType } from './types';

/**
 * Query parameters understood by the Transactions page.
 *
 * Dates are ISO (YYYY-MM-DD) in the URL because that is what callers outside the app can
 * construct — notably the payment-received email, which links providers straight to the
 * payments it is telling them about. Internally the search bar wants a `-`-separated
 * `MM/DD/YYYY` range, and `createSearchConditionsFromDateRange` splits that string on `-`,
 * so ISO dates cannot be passed through unconverted.
 */
export const TRANSACTION_URL_PARAMS = { TYPE: 'type', FROM: 'from', TO: 'to' };

// A Map rather than an object literal: a plain-object lookup resolves inherited keys, so
// `type=__proto__` or `type=constructor` would come back truthy instead of unrecognized.
const URL_TYPE_TO_TRANSACTION_TYPE = new Map([
  ['revenue', TransactionType.REVENUE],
  ['expense', TransactionType.EXPENSE],
  ['any', TransactionType.ANY],
]);

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const pad = (value) => String(value).padStart(2, '0');

/**
 * Reformats a strict ISO date as the `MM/DD/YYYY` the search bar expects, or null.
 *
 * Rearranges the string rather than round-tripping through a Date, because `new Date('2026-08-01')`
 * parses as UTC midnight and renders as the previous day in any negative offset — which is most of
 * our providers. The UTC construction below is only used to reject impossible dates such as
 * 2026-02-30, never to produce the output.
 *
 * Output must stay in step with `DefaultDateFormat` in helpers/dates.
 */
const toDisplayDate = (isoDate) => {
  const match = ISO_DATE_PATTERN.exec(isoDate ?? '');
  if (!match) return null;

  const [, year, month, day] = match;
  const asUtc = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  const isRealDate =
    asUtc.getUTCFullYear() === Number(year) &&
    asUtc.getUTCMonth() === Number(month) - 1 &&
    asUtc.getUTCDate() === Number(day);

  return isRealDate ? `${month}/${day}/${year}` : null;
};

/** Today in the viewer's own timezone, as ISO. */
const todayIsoDate = () => {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/**
 * Reads filter state out of the URL.
 *
 * Returns nulls rather than defaults when a parameter is absent or unparseable, so the caller
 * can tell "not specified" from "specified as Any" and leave its own defaults alone. An
 * unrecognised type or malformed date is ignored rather than throwing — a bad link should
 * still show the page.
 *
 * @param {URLSearchParams} searchParams
 * @param {string} [today] ISO date used to close an open-ended range; defaults to today.
 */
export function parseTransactionFilters(searchParams, today = todayIsoDate()) {
  if (!searchParams) return { transactionType: null, searchField: null, searchText: null };

  const rawType = searchParams.get(TRANSACTION_URL_PARAMS.TYPE);
  const transactionType = rawType ? (URL_TYPE_TO_TRANSACTION_TYPE.get(rawType.toLowerCase()) ?? null) : null;

  const from = toDisplayDate(searchParams.get(TRANSACTION_URL_PARAMS.FROM));
  if (!from) return { transactionType, searchField: null, searchText: null };

  // A missing `to` means "from then until now". A `to` that is present but unusable is a broken
  // link — reject the range rather than silently widening it to today, which would show a period
  // the caller never asked for.
  const rawTo = searchParams.get(TRANSACTION_URL_PARAMS.TO);
  const to = rawTo === null ? toDisplayDate(today) : toDisplayDate(rawTo);
  if (!to) return { transactionType, searchField: null, searchText: null };

  return { transactionType, searchField: 'date', searchText: `${from} - ${to}` };
}

/** Whether the URL asked for anything at all, i.e. whether an initial search should run. */
export function hasTransactionFilters(filters) {
  return Boolean(filters?.transactionType || filters?.searchText);
}
